// Effects for the audio editor. Everything is rendered in the browser with
// an OfflineAudioContext (the same Web Audio API the editor already uses),
// so the result is a normal audio file and no backend change is needed.
import { Channels, channelLength, noiseGate, peakOf, preventClipping, scaleChannels } from "./audioEdits";

export type EffectSettings =
  | { kind: "rumble"; cutoffHz: number }
  | { kind: "eq"; lowDb: number; midDb: number; highDb: number }
  | { kind: "compressor"; thresholdDb: number; ratio: number; knee: number; attack: number; release: number }
  | { kind: "reverb"; seconds: number; brightness: number; mix: number }
  | { kind: "echo"; delayMs: number; feedback: number; mix: number }
  | { kind: "gate"; thresholdDb: number };

export type EffectKind = EffectSettings["kind"];

export interface EffectPreset<S extends EffectSettings = EffectSettings> {
  label: string;
  settings: S;
}

type PresetMap = { [K in EffectKind]: EffectPreset<Extract<EffectSettings, { kind: K }>>[] };

export const EFFECT_PRESETS: PresetMap = {
  rumble: [
    { label: "Light", settings: { kind: "rumble", cutoffHz: 80 } },
    { label: "Strong", settings: { kind: "rumble", cutoffHz: 140 } },
  ],
  eq: [
    { label: "Warmer", settings: { kind: "eq", lowDb: 3, midDb: 0, highDb: -2 } },
    { label: "Brighter", settings: { kind: "eq", lowDb: -1, midDb: 0, highDb: 4 } },
    { label: "Vocal presence", settings: { kind: "eq", lowDb: -2, midDb: 3, highDb: 1 } },
  ],
  compressor: [
    { label: "Gentle", settings: { kind: "compressor", thresholdDb: -18, ratio: 2, knee: 10, attack: 0.01, release: 0.25 } },
    { label: "Vocal", settings: { kind: "compressor", thresholdDb: -24, ratio: 4, knee: 6, attack: 0.005, release: 0.2 } },
    { label: "Punchy", settings: { kind: "compressor", thresholdDb: -20, ratio: 6, knee: 4, attack: 0.003, release: 0.1 } },
  ],
  reverb: [
    { label: "Room", settings: { kind: "reverb", seconds: 0.6, brightness: 0.5, mix: 0.2 } },
    { label: "Hall", settings: { kind: "reverb", seconds: 2.2, brightness: 0.4, mix: 0.25 } },
    { label: "Plate", settings: { kind: "reverb", seconds: 1.2, brightness: 0.8, mix: 0.2 } },
  ],
  echo: [
    { label: "Short", settings: { kind: "echo", delayMs: 120, feedback: 0.25, mix: 0.3 } },
    { label: "Long", settings: { kind: "echo", delayMs: 380, feedback: 0.4, mix: 0.35 } },
  ],
  gate: [
    { label: "Light", settings: { kind: "gate", thresholdDb: -50 } },
    { label: "Strong", settings: { kind: "gate", thresholdDb: -38 } },
  ],
};

const getOfflineContextClass = (): typeof OfflineAudioContext | undefined =>
  (typeof window !== "undefined" &&
    ((window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext)) ||
  undefined;

// How much extra length an effect needs at the end (reverb/echo tails).
const tailSeconds = (s: EffectSettings) => {
  if (s.kind === "reverb") return s.seconds;
  if (s.kind === "echo" && s.feedback > 0 && s.mix > 0) {
    const repeats = Math.ceil(Math.log(0.001) / Math.log(Math.min(0.95, s.feedback)));
    return Math.min(6, (s.delayMs / 1000) * Math.max(1, repeats));
  }
  return 0;
};

// Deterministic, so the same settings always give the same result.
const makeImpulse = (ctx: BaseAudioContext, seconds: number, brightness: number) => {
  const length = Math.max(1, Math.round(seconds * ctx.sampleRate));
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  let seed = 12345;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const decay = 2 + (1 - brightness) * 3;
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    let last = 0;
    for (let i = 0; i < length; i++) {
      const noise = random() * 2 - 1;
      // Simple low-pass on the noise: darker reverbs have less high end.
      last = last + (noise - last) * (0.2 + 0.8 * brightness);
      data[i] = last * Math.pow(1 - i / length, decay);
    }
  }
  return impulse;
};

const render = async (input: Channels, sampleRate: number, settings: EffectSettings): Promise<Channels> => {
  const OfflineCtx = getOfflineContextClass();
  if (!OfflineCtx) throw new Error("This browser can't render audio effects.");
  const length = channelLength(input);
  const outLength = length + Math.round(tailSeconds(settings) * sampleRate);
  const ctx = new OfflineCtx(input.length, Math.max(1, outLength), sampleRate);

  const source = ctx.createBufferSource();
  const buffer = ctx.createBuffer(input.length, Math.max(1, length), sampleRate);
  input.forEach((data, ch) => buffer.getChannelData(ch).set(data.subarray(0, buffer.length)));
  source.buffer = buffer;

  if (settings.kind === "rumble") {
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = settings.cutoffHz;
    hp.Q.value = Math.SQRT1_2;
    source.connect(hp).connect(ctx.destination);
  } else if (settings.kind === "eq") {
    const low = ctx.createBiquadFilter();
    low.type = "lowshelf";
    low.frequency.value = 200;
    low.gain.value = settings.lowDb;
    const mid = ctx.createBiquadFilter();
    mid.type = "peaking";
    mid.frequency.value = 1500;
    mid.Q.value = 1;
    mid.gain.value = settings.midDb;
    const high = ctx.createBiquadFilter();
    high.type = "highshelf";
    high.frequency.value = 5000;
    high.gain.value = settings.highDb;
    source.connect(low).connect(mid).connect(high).connect(ctx.destination);
  } else if (settings.kind === "compressor") {
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = settings.thresholdDb;
    comp.ratio.value = settings.ratio;
    comp.knee.value = settings.knee;
    comp.attack.value = settings.attack;
    comp.release.value = settings.release;
    source.connect(comp).connect(ctx.destination);
  } else if (settings.kind === "reverb") {
    const dry = ctx.createGain();
    dry.gain.value = 1 - settings.mix * 0.5;
    const wet = ctx.createGain();
    wet.gain.value = settings.mix;
    const convolver = ctx.createConvolver();
    convolver.buffer = makeImpulse(ctx, settings.seconds, settings.brightness);
    source.connect(dry).connect(ctx.destination);
    source.connect(convolver).connect(wet).connect(ctx.destination);
  } else if (settings.kind === "echo") {
    const delay = ctx.createDelay(2);
    delay.delayTime.value = settings.delayMs / 1000;
    const feedback = ctx.createGain();
    feedback.gain.value = Math.min(0.9, Math.max(0, settings.feedback));
    const wet = ctx.createGain();
    wet.gain.value = settings.mix;
    source.connect(ctx.destination);
    source.connect(delay);
    delay.connect(feedback).connect(delay);
    delay.connect(wet).connect(ctx.destination);
  } else {
    throw new Error(`Unknown effect: ${(settings as any).kind}`);
  }

  source.start(0);
  const rendered = await ctx.startRendering();
  return Array.from({ length: rendered.numberOfChannels }, (_, ch) => rendered.getChannelData(ch).slice());
};

// Returns new channels; the input is never modified.
export const renderEffect = async (input: Channels, sampleRate: number, settings: EffectSettings): Promise<Channels> => {
  if (settings.kind === "gate") {
    return noiseGate(input, sampleRate, { thresholdDb: settings.thresholdDb });
  }
  const output = await render(input, sampleRate, settings);
  if (settings.kind === "compressor") {
    // Compression lowers the level; bring the loudest point back to where it was.
    const inPeak = peakOf(input);
    const outPeak = peakOf(output);
    if (inPeak > 0 && outPeak > 0) return preventClipping(scaleChannels(output, Math.min(4, inPeak / outPeak)));
  }
  return preventClipping(output);
};
